import { useState } from 'react'
import { motion } from 'framer-motion'

/**
 * Contact Component
 * Redesigned contact form matching Introduction section width
 */
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    sector: '',
    message: '',
    file: null,
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  // Advanced validation rules
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return emailRegex.test(email)
  }

  const validateName = (name) => {
    // Name should be 2-50 characters, allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s'-]{2,50}$/
    return nameRegex.test(name.trim())
  }

  const validateCompany = (company) => {
    // Company is optional, but if provided, should be 2-100 characters
    if (!company.trim()) return true
    return company.trim().length >= 2 && company.trim().length <= 100
  }

  const validateMessage = (message) => {
    // Message should be 20-5000 characters
    const trimmed = message.trim()
    return trimmed.length >= 20 && trimmed.length <= 5000
  }

  const validateFile = (file) => {
    if (!file) return true // File is optional

    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/postscript',
      'application/illustrator'
    ]

    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' }
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Accepted: PDF, DOC, DOCX, JPG, PNG, AI, EPS' }
    }

    return { valid: true }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (!validateName(formData.name)) {
      newErrors.name = 'Name must be 2-50 characters and contain only letters, spaces, hyphens, or apostrophes'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Company validation (optional)
    if (formData.company && !validateCompany(formData.company)) {
      newErrors.company = 'Company name must be 2-100 characters'
    }

    // Sector validation
    if (!formData.sector) {
      newErrors.sector = 'Please select a sector'
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    } else if (!validateMessage(formData.message)) {
      if (formData.message.trim().length < 20) {
        newErrors.message = 'Message must be at least 20 characters'
      } else {
        newErrors.message = 'Message must be less than 5000 characters'
      }
    }

    // File validation
    if (formData.file) {
      const fileValidation = validateFile(formData.file)
      if (!fileValidation.valid) {
        newErrors.file = fileValidation.error
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target
    if (name === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0] || null,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitStatus(null)

    if (!validateForm()) {
      // Scroll to first error after a brief delay to ensure state is updated
      setTimeout(() => {
        const firstErrorField = Object.keys(errors)[0]
        if (firstErrorField) {
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`)
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            errorElement.focus()
          }
        }
      }, 100)
      return
    }

    setIsSubmitting(true)

    try {
      // Determine API endpoint based on environment
      // Use relative path for API - Vite proxy handles this in dev, server handles it in prod
      const apiUrl = '/api/send-email.php'

      // Prepare FormData for file upload support
      const formDataToSend = new FormData()
      formDataToSend.append('name', formData.name.trim())
      formDataToSend.append('company', formData.company.trim())
      formDataToSend.append('email', formData.email.trim())
      formDataToSend.append('sector', formData.sector)
      formDataToSend.append('message', formData.message.trim())
      formDataToSend.append('timestamp', new Date().toISOString())

      if (formData.file) {
        formDataToSend.append('file', formData.file)
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      })

      // Check if response is ok and has content
      if (!response.ok) {
        let errorMessage = 'Failed to send message. Please try again.'
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorMessage
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage
        }
        throw new Error(errorMessage)
      }

      // Get response text first to check if it's valid JSON
      const responseText = await response.text()
      let result

      try {
        result = JSON.parse(responseText)
      } catch (e) {
        console.error('Invalid JSON response:', responseText)
        throw new Error('Invalid response from server. Please try again.')
      }

      if (result.success) {
        setSubmitStatus('success')
        setErrorMessage('')
        setFormData({
          name: '',
          company: '',
          email: '',
          sector: '',
          message: '',
          file: null,
        })

        // Reset file input
        const fileInput = document.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.value = ''
        }

        // Reset status after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000)
      } else {
        throw new Error(result.message || 'Submission failed')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setSubmitStatus('error')
      setErrorMessage(error.message || 'Something went wrong. Please try again or contact us directly.')

      // Reset error status after 10 seconds
      setTimeout(() => {
        setSubmitStatus(null)
        setErrorMessage('')
      }, 10000)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section
      id="contact"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden border-t border-gray-200/50"
      aria-labelledby="contact-heading"
    >
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-50 rounded-full blur-3xl opacity-50 -mr-48 -mt-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-100 rounded-full blur-3xl opacity-30 -ml-48 -mb-48"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            id="contact-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Contact – Let's Talk Packaging Precision
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Whether you're launching a new product, expanding into global markets, or refining your packaging for compliance, we're here to help. Reach out to explore how Artwork Solution can support your brand with strategic, compliant, and visually compelling artwork.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Contact Information Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-8 text-white shadow-2xl h-full sticky top-24">
              <h3 className="text-2xl font-bold mb-8">Get in Touch</h3>
              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">Email</h4>
                      <p className="text-primary-100 text-sm">Direct inquiries</p>
                    </div>
                  </div>
                  <a
                    href="mailto:contactus@artworksolution.com"
                    className="text-primary-100 hover:text-white transition-colors text-sm block ml-16"
                  >
                    contactus@artworksolution.com
                  </a>
                </div>
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">LinkedIn</h4>
                      <p className="text-primary-100 text-sm">Connect with us</p>
                    </div>
                  </div>
                  <a
                    href="https://linkedin.com/in/artworksolution"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-100 hover:text-white transition-colors text-sm block ml-16"
                  >
                    linkedin.com/in/artworksolution
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="lg:col-span-8 bg-white rounded-3xl shadow-xl border-2 border-gray-100 hover:border-primary-200 hover:shadow-2xl transition-all duration-300 p-8 lg:p-12 space-y-6"
            noValidate
          >
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Form</h3>
              <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours.</p>
            </div>

            {/* Name and Company Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Name <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-5 py-3.5 rounded-xl border-2 ${errors.name
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-primary focus:ring-primary hover:border-primary-300'
                    } focus:outline-none focus:ring-2 transition-all`}
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Company / Brand
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-5 py-3.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary-300 transition-all"
                />
              </div>
            </div>

            {/* Email and Sector Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Email Address <span className="text-primary">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-5 py-3.5 rounded-xl border-2 ${errors.email
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-primary focus:ring-primary hover:border-primary-300'
                    } focus:outline-none focus:ring-2 transition-all`}
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="sector"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Sector of Interest <span className="text-primary">*</span>
                </label>
                <select
                  id="sector"
                  name="sector"
                  value={formData.sector}
                  onChange={handleChange}
                  className={`w-full px-5 py-3.5 rounded-xl border-2 ${errors.sector
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-primary focus:ring-primary hover:border-primary-300'
                    } focus:outline-none focus:ring-2 transition-all bg-white`}
                  aria-required="true"
                  aria-invalid={!!errors.sector}
                  aria-describedby={errors.sector ? 'sector-error' : undefined}
                >
                  <option value="">Select a sector</option>
                  <option value="pharma">Pharma</option>
                  <option value="food">Food</option>
                  <option value="fmcg">FMCG</option>
                  <option value="all">All</option>
                </select>
                {errors.sector && (
                  <p id="sector-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.sector}
                  </p>
                )}
              </div>
            </div>

            {/* Message Field */}
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Message / Brief <span className="text-primary">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-5 py-3.5 rounded-xl border-2 ${errors.message
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-200 focus:border-primary focus:ring-primary'
                  } focus:outline-none focus:ring-2 transition-all resize-none`}
                aria-required="true"
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? 'message-error' : undefined}
                placeholder="Tell us about your packaging artwork needs..."
              />
              {errors.message && (
                <p id="message-error" className="mt-2 text-sm text-red-600" role="alert">
                  {errors.message}
                </p>
              )}
              <div className="mt-2 text-xs text-gray-500">
                {formData.message.trim().length}/5000 characters
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label
                htmlFor="file"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Upload Option (for artwork briefs or reference files)
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="file"
                  name="file"
                  onChange={handleChange}
                  className={`w-full px-5 py-3.5 rounded-xl border-2 ${errors.file
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-primary focus:ring-primary'
                    } focus:outline-none focus:ring-2 transition-all bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100`}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.ai,.eps"
                />
              </div>
              {errors.file && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {errors.file}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Accepted formats: PDF, DOC, DOCX, JPG, PNG, AI, EPS (Max 10MB)
              </p>
              {formData.file && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{formData.file.name}</span>
                  <span className="text-gray-500">({(formData.file.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              )}
            </div>

            {/* Submit Status Messages */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-5 bg-green-50 border-2 border-green-200 rounded-xl text-green-800"
                role="alert"
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.</span>
                </div>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-5 bg-red-50 border-2 border-red-200 rounded-xl text-red-800"
                role="alert"
              >
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-semibold mb-1">Error submitting form</p>
                    <p className="text-sm">{errorMessage}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              aria-label="Start the conversation"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                'Start the Conversation'
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  )
}

export default Contact
